import React, { useState, useContext } from "react";
import { Box, Typography, IconButton, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Tooltip } from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion.js";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CommonDownload from "../../custom-components/CommonDownload.js";
import { useLocation, useNavigate } from "react-router-dom";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners.js";
import { userStoryToUseCase } from "../../../services/webServices.js";
import RenderTableDataInPDF from "../../../utils/RenderTableDataInPDF.js";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../../main-pages/MultiTestCaseGenerationPage.js";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI.js";
import { LoadingModalContext } from "../../main-pages/ServicePage.js";

const FunctionalSpecDocument = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const { setLoadingState } = useContext(LoadingModalContext);

  const {
    selectedUserStories,
    functionalSpecDocument,
    multipleUseCases,
    setMultipleUseCases,
    setMultiTestCases,
    setFunctionalSpecDocument, 
  } = useContext(MultipleTestCasesContext);

  const { testCaseLoaderState, setTestCaseLoaderState } =
    useContext(TestCasesLoaderContext);

  const defaultFSDExpanded =
    multipleUseCases.length === 0 &&
    Object.entries(functionalSpecDocument).length > 0;

  const flattenedFunctionalSpecDocument = {};
  Object.entries(functionalSpecDocument).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      flattenedFunctionalSpecDocument[key] = value;
    } else if (!!value && typeof value === "object") {
      flattenedFunctionalSpecDocument[key] = flattenObject(value);
    } else {
      flattenedFunctionalSpecDocument[key] = value;
    }
  });


  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState(
    flattenedFunctionalSpecDocument
  );

  const handleEditToggle = () => {
    setEditableData(flattenedFunctionalSpecDocument);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableData(flattenedFunctionalSpecDocument);
  };

  const handleSave = () => {
    setFunctionalSpecDocument(editableData);
    setIsEditing(false);
  };

  const handleChange = (field, value, index, subField) => {
    setEditableData((prev) => {
      const updated = { ...prev };

      if (Array.isArray(updated[field])) {
        const newArray = [...updated[field]]; 

        if (subField) {
          newArray[index] = { ...newArray[index], [subField]: value };
        } else if (typeof index === "number") {
          newArray[index] = value;
        } else {
          return { ...updated, [field]: value };
        }

        updated[field] = newArray;
      } else {
        updated[field] = value;
      }

      return updated;
    });
  };


  const renderFunctionalRequirementsTable = (requirements) => {
    if (!Array.isArray(requirements)) {
      return <p>Invalid Functional Requirements data</p>;
    }

    return (
      <Box sx={{ mb: isEditing ? 2 : 0 }}>
        <Typography
          variant="subtitle2"
          color="black"
          fontWeight="bold"
          sx={{ marginBottom: 1, fontSize: "14px", marginTop: 3 }}
        >
          Functional Requirements
        </Typography>

        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            maxHeight: isEditing ? 300 : "none",
            overflowY: isEditing ? "auto" : "visible",
          }}
        >
          <Table
            size="small"
            stickyHeader={isEditing}
            sx={{ border: "1px solid #ccc" }}
          >
            <TableHead>
              <TableRow>
                {["Requirement ID", "Feature", "Description", "User Story Reference"].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      backgroundColor: "#49A3FF",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "13px", 
                      border: "1px solid #ccc",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      minWidth:
                        header === "Requirement ID" ? 100 :
                          header === "Feature" ? 140 :
                            header === "Description" ? 400 : 
                              150, 
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {requirements.map((req, rowIndex) => (
                <TableRow key={rowIndex}>
                  {["Requirement ID", "Feature", "Description", "User Story Reference"].map((key) => (
                    <TableCell
                      key={key}
                      sx={{ border: "1px solid #ccc", verticalAlign: "top", textAlign: "center", fontSize: "12px" }} // 🔹 reduced cell font
                    >
                      {isEditing ? (
                        <TextField
                          value={req[key] || ""}
                          onChange={(e) =>
                            handleChange("Functional Requirements", e.target.value, rowIndex, key)
                          }
                          fullWidth
                          multiline
                          variant="outlined"
                          size="small"
                          minRows={2}
                          maxRows={8}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#fff",
                              padding: "4px",
                              fontSize: "13px", 
                            },
                            "& textarea": {
                              overflow: "hidden !important",
                              fontSize: "13px", 
                            },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "13px" }}>
                          {req[key]}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>
    );
  };


  const renderField = (fieldName, fieldValue) => {
    if (fieldName === "Functional Requirements") {
      return renderFunctionalRequirementsTable(fieldValue);
    }

    // Arrays
    if (Array.isArray(fieldValue)) {
      const currentArray = editableData[fieldName] || [];
      const arrayText = currentArray.map((item, i) => `${i + 1}. ${item}`).join("\n");

      return isEditing ? (
        <TextField
          label={fieldName}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={arrayText}
          onChange={(e) => {
            const lines = e.target.value
              .split("\n")
              .map(line => line.replace(/^\d+\.\s*/, ""));
            handleChange(fieldName, lines);
          }}

          sx={{ marginBottom: 2 }}
        />
      ) : (
        <>
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{ marginTop: 3, marginBottom: 1, fontSize: "14px" }}
          >
            {fieldName}
          </Typography>
          {currentArray.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ fontSize: "14px", color: "text.primary" }}
            >
              {`${index + 1}. ${item}`}
            </Typography>
          ))}
        </>
      );
    }

    // Primitive values
    if (typeof fieldValue === "string" || typeof fieldValue === "number") {
      return isEditing ? (
        <TextField
          label={fieldName}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={fieldValue}
          onChange={(e) => handleChange(fieldName, e.target.value)}
          sx={{ marginBottom: 2 }}
        />
      ) : (
        <>
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{
              marginTop: fieldName === "Overview" ? 0 : 3,
              marginBottom: 1,
              fontSize: "14px",
            }}
          >
            {fieldName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "14px" }}
            dangerouslySetInnerHTML={{
              __html: fieldValue.toString().replace(/\n/g, "<br>"),
            }}
          />
        </>
      );
    }
    return null;
  };


  const fsdDetails = (
    <Box sx={{ padding: "10px", position: "relative" }}>
      {!isEditing && (
        <Tooltip
          title="Edit FSD"
          placement="top"
          arrow
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -15], 
                },
              },
            ],
          }}
        >
          <IconButton
            onClick={handleEditToggle}
            sx={{ position: "absolute", top: -4, right: 8 }}
          >
            <PenIcon style={{ width: 20, height: 20 }} />
          </IconButton>
        </Tooltip>

      )}

      {Object.entries(editableData).map(([fieldName, fieldValue]) =>
        renderField(fieldName, fieldValue)
      )}

      {isEditing && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            background: "#fff",
            padding: "8px 0",
            borderTop: "1px solid #ddd",
            zIndex: 2
          }}
        >
          <Button
            sx={{ color: "#49A3FF", fontSize: "14px", textTransform: "none" }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#49A3FF", color: "white", fontSize: "14px", textTransform: "none", }}
            onClick={() => handleSave()}
          >
            Save
          </Button>
        </Box>
      )}

    </Box>
  );

  const handleDownload = () => setDownloadOpen(true);

  const handleGenerateUseCase = async () => {
    let progressHandler;
    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 13000,
      });
      progressHandler.start(
        selectedUserStories.length === 1
          ? "Generating Use Case"
          : "Generating Use Cases"
      );
      if (selectedUserStories.every((each) => !each)) return;

      setTestCaseLoaderState("Use Case");
      setMultipleUseCases([]);
      setMultiTestCases([]);

      const useCasePromises = selectedUserStories.map((eachUserStory) =>
        eachUserStory && Object.keys(eachUserStory).length > 0
          ? userStoryToUseCase({ userStory: { ...eachUserStory } })
          : Promise.resolve(null)
      );

      const responses = await Promise.all(useCasePromises);

      const updatedUseCases = responses.map((useCases) => {
        if (!useCases) return null;
        const formattedUseCase = {};
        Object.entries(useCases.data).map(([key, value]) => {
          if (Array.isArray(value)) return (formattedUseCase[key] = flattenArray(value));
          if (typeof value === "object" && !!value)
            return (formattedUseCase[key] = flattenObject(value));
          return (formattedUseCase[key] = value);
        });
        return formattedUseCase;
      });

      setMultipleUseCases(updatedUseCases);

      await progressHandler.finishAndClose();

      navigate("/dashboard/generate-multi/testcases/output", {
        state: {
          ...location.state,
          multipleUseCases: updatedUseCases,
          selectedMultipleUseCases: Array(updatedUseCases.length).fill(null),
          multipleTestCases: [],
          selectedMultipleTestCases: [],
        },
        replace: true,
      });
    } catch (error) {
      console.error(error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setTestCaseLoaderState(null);
      if (progressHandler) progressHandler.stop();
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const buttons =
    [
      {
        label: "Download",
        variant: "text",
        onClick: handleDownload,
        startIcon: <FileDownloadOutlinedIcon />,
      },
      {
        label: "Generate Use Case",
        variant: "contained",
        onClick: handleGenerateUseCase,
        disabled: testCaseLoaderState === "Use Case",
      },
    ];


  const formatValueForDoc = (value) => {
    if (Array.isArray(value)) {
      return value.map((item, index) => `${index + 1}. ${item}`).join("\n");
    } else if (typeof value === "string") {
      return value;
    }
    return value;
  };

  return (
    <Box sx={{ height: "100%", width: { xs: '100%', md: 966, lg: 1100 } }}>
      <CommonAccordion
        summary={"Functional Specification Document"}
        details={fsdDetails}
        buttons={buttons}
        type={"output"}
        fullScreenMode={true}
        defaultExpanded={defaultFSDExpanded}
      />

      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName={"Functional Specification Document"}
        serviceResponse={[
          {
            heading: "Overview",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Overview"]
            ),
          },
          {
            heading: "Scope and Objectives",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Scope and Objectives"]
            ),
          },
          {
            heading: "Functional Requirements",
            type: "table",
            data:
              Object.keys(flattenedFunctionalSpecDocument).length > 0
                ? RenderTableDataInPDF(
                  flattenedFunctionalSpecDocument,
                  "Functional Requirements"
                )
                : "",
          },
          {
            heading: "System Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["System Requirements"]
            ),
          },
          {
            heading: "User Interface Specifications",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["User Interface Specifications"]
            ),
          },
          {
            heading: "Workflows and Processes",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Workflows and Processes"]
            ),
          },
          {
            heading: "Data Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Data Requirements"]
            ),
          },
          {
            heading: "Non-Functional Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Non-Functional Requirements"]
            ),
          },
          {
            heading: "Assumptions and Constraints",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Assumptions and Constraints"]
            ),
          },
          {
            heading: "Acceptance Criteria",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Acceptance Criteria"]
            ),
          },
        ]}

        wordData={[
          {
            heading: "Overview",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Overview"]
            ),
          },
          {
            heading: "Scope and Objectives",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Scope and Objectives"]
            ),
          },
          {
            heading: "Functional Requirements",
            type: "table",
            data:
              Object.keys(flattenedFunctionalSpecDocument).length > 0
                ? RenderTableDataInPDF(
                  flattenedFunctionalSpecDocument,
                  "Functional Requirements"
                )
                : "",
          },
          {
            heading: "System Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["System Requirements"]
            ),
          },
          {
            heading: "User Interface Specifications",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["User Interface Specifications"]
            ),
          },
          {
            heading: "Workflows and Processes",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Workflows and Processes"]
            ),
          },
          {
            heading: "Data Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Data Requirements"]
            ),
          },
          {
            heading: "Non-Functional Requirements",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Non-Functional Requirements"]
            ),
          },
          {
            heading: "Assumptions and Constraints",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Assumptions and Constraints"]
            ),
          },
          {
            heading: "Acceptance Criteria",
            type: "text",
            data: formatValueForDoc(
              flattenedFunctionalSpecDocument["Acceptance Criteria"]
            ),
          },
        ]}
        documents={["docx", "pdf"]}
      />
    </Box>
  );
};

export default FunctionalSpecDocument;
